import type { InjectionKey, Ref } from 'vue';

/**
 * How a table that lists records opens one (`recordList`, `table.row_open`):
 * each gesture is the user's choice of where the record lands. The kit knows no
 * routes and no workspaces: it runs the table's own open handler and the host's
 * opener, provided under `NX_ROW_OPENER_KEY`, sends that handler's navigation to
 * the target. Without an opener everything opens in place.
 */

/** `here`: in place. `tab`: a browser tab. `split`: beside this page. `workspace`: a new workspace. */
export type NxRowOpenTarget = 'here' | 'tab' | 'split' | 'workspace';
export type NxRowOpenAction = NxRowOpenTarget | 'none';

export interface NxRowOpenSettings {
  single?: NxRowOpenAction;
  double?: NxRowOpenAction;
  middle?: NxRowOpenAction;
}

/** A place the host already has open and a record can go to, e.g. a tile beside this list. */
export interface NxRowOpenTile {
  id: string;
  label: string;
}

/**
 * Where the host opens a row. `split` is the gesture: beside this page, in a place
 * that is already there if the host prefers that. `new-split` always adds one.
 */
export type NxRowOpenPlace = Exclude<NxRowOpenTarget, 'here'> | 'new-split' | { tileId: string };

export type NxRowOpener = (place: NxRowOpenPlace, open: () => void) => void;

export const NX_ROW_OPENER_KEY: InjectionKey<NxRowOpener> = Symbol('nxRowOpener');

/**
 * Whether the host can open a row beside this page at all (its tiles, a new
 * tile, a new workspace). Off, the row menu offers only here and a browser tab.
 * Without it, it can whenever there is an opener.
 */
export const NX_ROW_OPEN_BESIDE_KEY: InjectionKey<Readonly<Ref<boolean>>> =
  Symbol('nxRowOpenBeside');

/** The places the row menu offers besides its fixed entries. */
export const NX_ROW_OPEN_TILES_KEY: InjectionKey<Readonly<Ref<readonly NxRowOpenTile[]>>> =
  Symbol('nxRowOpenTiles');

const ACTIONS: readonly NxRowOpenAction[] = ['none', 'here', 'tab', 'split', 'workspace'];

function actionOf(value: unknown, fallback: NxRowOpenAction): NxRowOpenAction {
  return ACTIONS.includes(value as NxRowOpenAction) ? (value as NxRowOpenAction) : fallback;
}

/** The gestures of `table.row_open` with the kit's defaults: as a table has always behaved. */
export function rowOpenOf(settings: unknown): Required<NxRowOpenSettings> {
  const doc = (typeof settings === 'object' && settings ? settings : {}) as NxRowOpenSettings;
  return {
    single: actionOf(doc.single, 'none'),
    double: actionOf(doc.double, 'here'),
    middle: actionOf(doc.middle, 'tab'),
  };
}

export function createRowOpen<Row>(options: {
  enabled: () => boolean;
  actions: () => Required<NxRowOpenSettings>;
  opener: NxRowOpener | null;
  open: (rowId: string, columnId: string, row: Row) => void;
}) {
  function openAt(
    target: NxRowOpenTarget | NxRowOpenPlace,
    rowId: string,
    columnId: string,
    row: Row
  ): void {
    const open = () => options.open(rowId, columnId, row);
    if (target === 'here' || !options.opener) open();
    else options.opener(target, open);
  }

  function run(action: NxRowOpenAction, rowId: string, columnId: string, row: Row): void {
    if (action !== 'none') openAt(action, rowId, columnId, row);
  }

  return {
    openAt,
    /** A plain click; the table's navigation has already ruled out a double-click. */
    single(rowId: string, columnId: string, row: Row): void {
      if (options.enabled()) run(options.actions().single, rowId, columnId, row);
    },
    /** False: this table does not map gestures, the caller keeps its double-click. */
    double(rowId: string, columnId: string, row: Row): boolean {
      if (!options.enabled()) return false;
      run(options.actions().double, rowId, columnId, row);
      return true;
    },
    /** False: leave the middle button to the browser. */
    middle(rowId: string, columnId: string, row: Row): boolean {
      if (!options.enabled()) return false;
      const { middle } = options.actions();
      if (middle === 'none') return false;
      run(middle, rowId, columnId, row);
      return true;
    },
  };
}
